import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';

import { FormControl, IconButton2, TextControl } from '@clatter/ui';
import { useMountedEffect } from '@clatter/platform';
import { CircularLoader } from '../../shared';

const StyledWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  input {
    display: inline-block;
    min-width: 30vw;
  }

  h2 {
    line-height: 1.3;
  }

  &.element {
    button {
      margin-bottom: 10px;
    }
  }

  .buttons {
    display: flex;
    flex-wrap: nowrap;
  }
`;

const allowedTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'p', 'span'];
export const EditableTextElementV2 = ({
  element,
  customValidator,
  label = 'Title',
  initialValue = '',
  isLoading = false,
  errorMessage = '',
  isEditing = false,
  disableEdit = false,
  onAction,
  children,
}) => {
  const [editedValue, setEditedValue] = useState('');
  const [localError, setLocalError] = useState(null);

  const initialValueRef = useRef(null);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  const isConfirmDisabled = useMemo(
    () => localError || editedValue === initialValueRef.current,
    [localError, initialValueRef?.current, editedValue],
  );

  //region METHODS
  const handleEditClick = () => {
    if (!disableEdit) {
      onAction({ type: 'EDIT' });
    }
  };

  const handleConfirmClick = () => {
    onAction({ type: 'UPDATE', payload: editedValue });
  };

  const handleCancelClick = () => {
    setEditedValue(initialValue);
    onAction({ type: 'CANCEL' });
  };

  const validateField = (e) => {
    if (customValidator && typeof customValidator === 'function') {
      const error = customValidator(e.target.value);
      if (error) {
        setLocalError(error);
        return;
      }
    }

    setLocalError(null);
    return null;
  };

  const handleInputChange = (e) => {
    validateField(e);
    setEditedValue(e.target.value);
  };
  //endregion

  //region EFFECTS
  useMountedEffect(() => {
    initialValueRef.current = initialValue;
    setEditedValue(initialValue?.trim());
  }, [initialValue]);

  useMountedEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  useMountedEffect(() => {
    if (typeof errorMessage === 'string' && errorMessage?.trim()) {
      setLocalError(errorMessage);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (disableEdit) {
      return;
    }
    
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        if (isConfirmDisabled) {
          handleCancelClick();
        } else {
          handleConfirmClick();
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isConfirmDisabled, handleConfirmClick, disableEdit]);
  //endregion

  const renderEditableContent = () => (
    <FormControl label={label} error={{ message: localError }}>
      <StyledWrapper ref={wrapperRef}>
        <TextControl
          ref={inputRef}
          type="text"
          value={editedValue}
          hasError={localError}
          onBlur={validateField}
          onChange={handleInputChange}
          fullWidth
        />

        <div className="buttons">
          {isLoading ? (
            <CircularLoader />
          ) : (
            <>
              <IconButton2 onClick={handleConfirmClick} disabled={isConfirmDisabled || isLoading} testId="DoneButton">
                <DoneIcon />
              </IconButton2>
              <IconButton2 onClick={handleCancelClick} disabled={isLoading} testId="CloseButton">
                <CloseIcon />
              </IconButton2>
            </>
          )}
        </div>
      </StyledWrapper>
    </FormControl>
  );

  const renderElement = (value) => React.createElement(element, null, value);

  return (
    <>
      {isEditing ? (
        renderEditableContent()
      ) : (
        <StyledWrapper className="element" onClick={handleEditClick}>
          {children ? children : renderElement(editedValue)}
          {!disableEdit && (
            <IconButton2 onClick={handleEditClick} testId="EditButton">
              <EditIcon />
            </IconButton2>
          )}
        </StyledWrapper>
      )}
    </>
  );
};
